'use client'

import Link from 'next/link'
import { useState } from 'react'
import { FormMessage } from '../FormMessage'
import { Button } from '../ui/button'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'

import { orgUrlInUse, submitOrganization } from '@/app/actions/organization'
import { updateUser } from '@/app/actions/user'
import { Checkbox } from '@/blocks/Form/Checkbox'
import { Select } from '@/blocks/Form/Select'
import { Text } from '@/blocks/Form/Text'
import { Textarea } from '@/blocks/Form/Textarea'
import { Upload } from '@/blocks/Form/Upload'
import { FormAccessRequired } from '@/components/FormAccessRequired'
import { formValidation } from '@/utilities/formValidation'
import { orgUrlInUseMessage } from '@/utilities/organizationUrl'

import type { User } from '@/payload-types'

const OrgForm = ({ payloadUser }: { payloadUser: User | null }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const methods = useForm<FieldValues>({
    defaultValues: {
      firstName: payloadUser?.firstName ?? '',
      lastName: payloadUser?.lastName ?? '',
      email: payloadUser?.email ?? '',
      phone: payloadUser?.profile?.mobile ?? '',
    },
  })

  const onSubmit = async (data: FieldValues) => {
    if (!payloadUser) return

    setIsSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(false)

    try {
      const form = document.getElementById('org-form') as HTMLFormElement
      const formData = new FormData(form)

      // FormData from form element will include all inputs including files
      // But we need to ensure all text fields are included (in case they're not in native inputs)
      // Since we're using react-hook-form, we'll append the data to ensure it's there
      formData.set('organizationName', data.organizationName || '')
      formData.set('websiteUrl', data.websiteUrl || '')
      formData.set('shortDescription', data.shortDescription || '')
      formData.set('organizationDescription', data.organizationDescription || '')
      formData.set('firstName', data.firstName || '')
      formData.set('lastName', data.lastName || '')
      formData.set('email', data.email || '')
      formData.set('phone', data.phone || '')
      formData.set('relationToOrganization', data.relationToOrganization || '')
      formData.set('logoImage', data.logoImage)
      formData.set('heroImage', data.heroImage)

      // Update Payload user profile (same as FormBlock): WorkOS sync + PATCH users
      const hasUserProfileFields =
        payloadUser?.id && ('firstName' in data || 'lastName' in data || 'email' in data)
      if (hasUserProfileFields && payloadUser) {
        const dataForSubmit: User = {
          ...payloadUser,
          firstName: data.firstName ?? payloadUser.firstName ?? '',
          lastName: data.lastName ?? payloadUser.lastName ?? '',
          // email: data.email ?? payloadUser.email ?? '',
          profile: {
            ...payloadUser.profile,
            mobile: data.phone ?? payloadUser.profile?.mobile ?? '',
          },
        }

        try {
          const res = await updateUser({
            ...dataForSubmit,
          })

          if (res.status === 'error') {
            throw new Error(res.message)
          }
        } catch (error) {
          setSubmitError(error instanceof Error ? error.message : 'Failed to update profile')
          setIsSubmitting(false)
          return
        }
      }

      const status = await submitOrganization(formData)

      if (!status.success) {
        throw new Error(status.message ?? 'Failed to submit organization')
      }

      setSubmitSuccess(true)
      // Reset all fields: clear org fields, restore user contact info if logged in, clear file refs
      methods.reset({
        organizationName: '',
        websiteUrl: '',
        shortDescription: '',
        organizationDescription: '',
        relationToOrganization: '',
        logoImage: null,
        heroImage: null,
        termsAndConditions: false,
        firstName: payloadUser?.firstName ?? '',
        lastName: payloadUser?.lastName ?? '',
        email: payloadUser?.email ?? '',
        phone: payloadUser?.profile?.mobile ?? '',
      })

      // Clear file input elements so the same file can be selected again
      const logoInput = form?.querySelector('input[id="logoImage"]') as HTMLInputElement
      const heroInput = form?.querySelector('input[id="heroImage"]') as HTMLInputElement
      if (logoInput) logoInput.value = ''
      if (heroInput) heroInput.value = ''
    } catch (error: unknown) {
      setSubmitError(
        error instanceof Error ? error.message : 'An error occurred while submitting the form',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <form
        id="org-form"
        onSubmit={methods.handleSubmit(onSubmit)}
        className="relative flex h-full flex-col"
      >
        {payloadUser ? (
          <div className="relative flex h-full flex-col border-t border-border pt-6 font-family-body">
            <div className="flex flex-1 flex-col items-start justify-between pb-8 md:flex-row">
              <h3 className="pb-8 font-family-body text-2xl text-neutral-500 md:pb-0 md:pl-6">
                Other Great Organizations
              </h3>
              <div className="md:w-1/2 md:px-8">
                <h2 className="font-family-body text-2xl font-semibold text-foreground">
                  Add an organization
                </h2>
                <p className="mt-3 font-body text-card-foreground">
                  Know of a MAHA organization we should feature? Submit one for review, and
                  we&apos;ll take a look at the details. Only nonprofit or advocacy organizations
                  are eligible to be featured. Thank you for being part of this movement!
                </p>
              </div>
            </div>
            <div className="flex w-full flex-1 flex-col justify-end border-t border-border pt-6 md:flex-row">
              <div className="flex flex-col justify-between self-start md:w-1/2">
                <div className="md:pl-6">
                  <h3 className="pb-8 font-family-body text-lg font-semibold text-foreground">
                    Organization Information
                  </h3>
                  <div>
                    <Text
                      name="organizationName"
                      label="Organization Name"
                      blockType="text"
                      placeholder="Enter a name"
                      errors={methods.formState.errors}
                      register={methods.register}
                      required
                      validation={{
                        ...formValidation.organizationName,
                      }}
                    />
                    <div className="h-8" />
                    <Text
                      name="websiteUrl"
                      label="Website URL"
                      blockType="text"
                      placeholder="Enter a URL"
                      errors={methods.formState.errors}
                      register={methods.register}
                      required
                      validation={{
                        ...formValidation.websiteUrl,
                        validate: {
                          format: formValidation.websiteUrl.validate,
                          unique: async (value: string) => {
                            if (!value?.trim()) return true
                            const inUse = await orgUrlInUse(value)
                            return inUse ? orgUrlInUseMessage : true
                          },
                        },
                      }}
                    />
                    <div className="h-8" />
                    <Textarea
                      name="shortDescription"
                      label="Short Description"
                      blockType="text"
                      errors={methods.formState.errors}
                      register={methods.register}
                      placeholder="Enter a short description"
                      description="Enter a short description. Recommended length of approximately 100 - 200 characters. If not provided, the first few sentences will be used of the description."
                      {...({ validation: formValidation.shortDescription } as FieldValues)}
                    />
                    <div className="h-8" />
                    <Select
                      name="nonprofitAdvocacyVerification"
                      label="Is this a non-profit or advocacy organization? (Only nonprofit or advocacy organizations are eligible to be featured.)"
                      blockType="select"
                      errors={methods.formState.errors}
                      required
                      control={methods.control}
                      placeholder="Select an option"
                      options={[
                        { label: 'Yes', value: 'yes' },
                        { label: 'No', value: 'no' },
                      ]}
                      validation={formValidation.organizationNonprofitAdvocacyVerification}
                    />
                    <div className="h-8" />
                  </div>
                </div>
              </div>
              <div className="mb-8 flex flex-col justify-between self-start md:w-1/2">
                <div className="md:px-8">
                  <Textarea
                    name="organizationDescription"
                    label="Organization Description"
                    blockType="text"
                    errors={methods.formState.errors}
                    register={methods.register}
                    placeholder="Enter a description"
                    description="Enter a description. Recommended length of approximately 400 - 700 characters."
                    required
                    rows={10}
                    {...({ validation: formValidation.organizationDescription } as FieldValues)}
                  />
                </div>
              </div>
            </div>
            <div className="flex-1 border-t border-border pt-6">
              <div className="flex h-full flex-col justify-between md:flex-row md:pl-6">
                <div className="md:w-1/2">
                  <h3 className="pb-3 font-family-body text-lg font-semibold text-foreground">
                    Organization Images
                  </h3>
                  <p className="text-card-foreground">
                    We will need two images for an organization. A logo image and an image to be
                    used for a card, both of which examples can be seen on this page.
                  </p>
                </div>
                <div className="mt-8 md:mt-auto md:w-1/2 md:px-8">
                  <div className="mb-8 w-full">
                    <Upload
                      name="logoImage"
                      label="Logo"
                      defaultValue=""
                      width="full"
                      errors={methods.formState.errors}
                      register={methods.register}
                      control={methods.control}
                      resetSignal={submitSuccess}
                      required
                    />
                  </div>
                  <div className="mb-8 w-full">
                    <Upload
                      name="heroImage"
                      label="Card / Header"
                      defaultValue=""
                      width="full"
                      errors={methods.formState.errors}
                      register={methods.register}
                      control={methods.control}
                      resetSignal={submitSuccess}
                      required={false}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-1 border-t border-border pt-6">
              <div className="flex h-full flex-col justify-between md:flex-row md:pl-6">
                <div className="md:w-1/2">
                  <h3 className="pb-3 font-family-body text-lg font-semibold text-foreground">
                    Your Information
                  </h3>
                  <p className="text-card-foreground">
                    Your information is needed so we can contact you in regards of the submission
                    and update you on the review process.
                    <span className="mt-2 block text-xs text-muted-foreground">
                      * Any changes to your information will update your MAHA user details.
                    </span>
                  </p>
                </div>
                <div className="mt-8 md:mt-auto md:w-1/2 md:px-8">
                  <div className="mb-8 w-full">
                    <Text
                      name="firstName"
                      label="First Name"
                      blockType="text"
                      placeholder="Enter first name"
                      errors={methods.formState.errors}
                      register={methods.register}
                      defaultValue={methods.getValues('firstName')}
                      required
                      validation={formValidation.firstName}
                    />
                    <div className="h-8" />
                    <Text
                      name="lastName"
                      label="Last Name"
                      blockType="text"
                      placeholder="Enter last name"
                      errors={methods.formState.errors}
                      register={methods.register}
                      defaultValue={methods.getValues('lastName')}
                      required
                      validation={formValidation.lastName}
                    />
                    <div className="h-8" />
                    <Text
                      name="email"
                      label="Email"
                      blockType="text"
                      type="email"
                      placeholder="Enter an email address"
                      errors={methods.formState.errors}
                      register={methods.register}
                      defaultValue={methods.getValues('email')}
                      required
                      validation={formValidation.email}
                      disabled={methods.getValues('email') ? true : false}
                    />
                    <div className="h-8" />
                    <Text
                      name="phone"
                      label="Phone"
                      blockType="text"
                      type="tel"
                      placeholder="Enter a phone number"
                      errors={methods.formState.errors}
                      register={methods.register}
                      defaultValue={methods.getValues('phone')}
                      required={false}
                      validation={formValidation.phone}
                    />
                    <div className="h-8" />
                    <Textarea
                      name="relationToOrganization"
                      label="What is your relation to the organization?"
                      blockType="text"
                      errors={methods.formState.errors}
                      register={methods.register}
                      placeholder="Enter your relation to the organization"
                      required
                      validation={formValidation.relationToOrganization}
                    />
                    <div className="h-8" />
                    <Checkbox
                      name="termsAndConditions"
                      label={
                        <>
                          By checking this box, you consent to receive official MAHA® communications
                          and agree to our{' '}
                          <Link href="/privacy-policy" className="underline hover:no-underline">
                            Privacy Policy
                          </Link>{' '}
                          and{' '}
                          <Link href="/terms-of-service" className="underline hover:no-underline">
                            Terms of Service
                          </Link>
                          . Your information will be stored securely and used solely for authorized
                          MAHA® purposes.
                        </>
                      }
                      defaultValue={true}
                      errors={methods.formState.errors}
                      blockType="checkbox"
                      register={methods.register}
                      required
                    />
                    <Button type="submit" className="mt-3 w-full" disabled={isSubmitting}>
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                    {submitSuccess ? (
                      <FormMessage
                        type="success"
                        message="Organization submitted successfully! It will be reviewed before being published."
                        className="mt-4"
                      />
                    ) : submitError ? (
                      <FormMessage
                        type="error"
                        message={
                          submitError ?? 'An error occurred while submitting the organization.'
                        }
                        className="mt-4"
                      />
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
            {/* ! I am not sure what to do with this button, it has no real clean spot to sit when there are variable window heights. I thought using it as a temporary bouncing alert type thing might work and then it hides? TBD. */}
            {/* <motion.div
            // i want the button to scale up and down in a loop
            className="border border-border text-border rounded-full my-8 fixed bottom-5 ml-2 left-1/4 px-4 py-2 flex items-center gap-2"
            animate={{
              scale: [1, 1.2, 1],
              transition: {
                duration: 5,
                ease: 'easeInOut',
                repeat: Infinity,
                repeatType: 'loop',
              },
            }}
          >
            <ArrowDown className="w-4 h-4" />
            Scroll down
          </motion.div> */}
          </div>
        ) : (
          <div>
            <div className="flex flex-1 flex-col items-start justify-between pb-8 md:flex-row">
              <h3 className="pb-8 font-family-body text-2xl text-neutral-500 md:pb-0 md:pl-6">
                Other Great Organizations
              </h3>
              <div className="md:w-1/2 md:px-8">
                <h2 className="font-family-body text-2xl font-semibold text-foreground">
                  Add an organization
                </h2>
                <p className="mt-3 text-card-foreground">
                  Know of a MAHA organization we should feature? Submit it and we&apos;ll review the
                  details. Thanks for being part of this movement!
                </p>
              </div>
            </div>
            <div className="border-t border-border pt-6">
              <FormAccessRequired />
            </div>
          </div>
        )}
      </form>
    </FormProvider>
  )
}

export default OrgForm
